import { useMutation } from '@apollo/client';
import { Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersistentSnackbar } from '../helpers';
import { UpdateViewDocument, ViewThumbnail } from './update.generated';

export const useUpdateView = () => {
  const { enqueueSnackbar } = useSnackbar();
  const persistentSnackbar = usePersistentSnackbar();
  const { t } = useTranslation();

  const [mutationFn, mutationResult] = useMutation(UpdateViewDocument);

  const wrappedFn = useCallback<typeof mutationFn>(
    (options) =>
      mutationFn({
        onCompleted: () => {
          enqueueSnackbar(
            <Typography color="inherit">{t('api.views.update_success')}</Typography>,
            {
              variant: 'success',
            },
          );
        },

        onError: (_error: unknown) => {
          persistentSnackbar(t('api.views.update_error'), { reload: true });
        },

        update(cache, { data }) {
          if (data?.updateView?.view == null) return;
          cache.updateFragment(
            { id: cache.identify(data.updateView.view), fragment: ViewThumbnail, overwrite: true },
            (data) => ({
              __typename: 'View' as const,
              thumbnailUri: options?.variables?.patch.thumbnailUri ?? data?.thumbnailUri,
            }),
          );
        },

        ...options,
      }),
    [enqueueSnackbar, mutationFn, persistentSnackbar, t],
  );

  return [wrappedFn, mutationResult] as const;
};
